import React from 'react';
import { FailedTrackedQuery } from '@bilderit/offline-link';
import { useFailedTrackedQueries } from '@/hooks/useFailedTrackedQueries';

export const ADD_CHECKLIST_OPERATION = 'AddChecklist';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The id of the checklist a failed `AddChecklist` send carries. The entry is
 * JSON read back from the device's storage, so nothing about its shape is
 * taken for granted.
 */
export const failedChecklistSendId = (
  entry: Pick<FailedTrackedQuery, 'variables'>
): string | undefined => {
  const input = isRecord(entry.variables) ? entry.variables.input : undefined;
  const id = isRecord(input) ? input.id : undefined;
  return typeof id === 'string' ? id : undefined;
};

/**
 * "<template title> · <machine name>" for a failed `AddChecklist` send, from
 * the optimistic Checklist stored with it (see `useSubmitChecklist`); a part
 * it does not have is left out, and `undefined` is returned when it has none.
 */
export const describeFailedChecklistSend = (
  entry: Pick<FailedTrackedQuery, 'context'>
): string | undefined => {
  const optimistic: unknown = entry.context.optimisticResponse;
  const checklist = isRecord(optimistic) ? optimistic.addChecklist : undefined;
  if (!isRecord(checklist)) return undefined;

  const machine = isRecord(checklist.machine) ? checklist.machine : undefined;
  const parts = [checklist.template_title, machine?.name].filter(
    (part): part is string => typeof part === 'string' && part !== ''
  );
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

/**
 * Ids of the checklists whose send the server rejected. A "Mis checklists"
 * row with one of them is no longer waiting to sync: it is shown as "No
 * enviado" and does not count as "Hecho hoy" until the send is retried (which
 * puts it back in the queue, and out of this set) or discarded.
 */
export const useFailedChecklistSendIds = (): ReadonlySet<string> => {
  const failed = useFailedTrackedQueries();

  return React.useMemo(() => {
    const ids = new Set<string>();
    for (const entry of failed) {
      if (entry.operationName !== ADD_CHECKLIST_OPERATION) continue;
      const id = failedChecklistSendId(entry);
      if (id !== undefined) ids.add(id);
    }
    return ids;
  }, [failed]);
};
